import './styles/styles.scss';
import 'core-js/stable';
import 'regenerator-runtime/runtime';

import './faroInstrumentalisation';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { createRoot } from 'react-dom/client';
import { StrictMode } from 'react';
import { bugsnagApiKey, enableBugSnag, nodeEnv } from '@/shared/constants';

import { xsrfTokenCheck } from './features/scaffolding/services';
import Loading from './Loading';
// Root and Loading need to stay out here for HMR purposes
import Root from './Root';
import { launchDarkly } from './shared/constants';
import { configureHttp, configureStore } from './util';

import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import EnvironmentTab from './EnvironmentTab';
import pkg from '../package.json';
// setup
const appElement = document.getElementById('app');

if (!appElement) {
  throw new Error('Failed to find the app element');
}

const root = createRoot(appElement);
const store = configureStore();

configureHttp({});

// React's dev build records a performance.measure (with a props diff) for every
// component re-render whose props changed. Chrome never evicts those entries,
// so a live game page grows to several GB and crashes ("Aw, Snap").
if (import.meta.env.DEV) {
  setInterval(() => performance.clearMeasures(), 10_000);
}

if (enableBugSnag) {
  Bugsnag.start({
    apiKey: bugsnagApiKey,
    plugins: [new BugsnagPluginReact()],
    appVersion: pkg.version,
    onError: (event) => {
      // Don't send errors from the LaunchDarkly SDK
      if (event.request?.url?.includes('launchdarkly')) {
        return false;
      }
      return true;
    },
    releaseStage: nodeEnv.startsWith('pr')
      ? 'production'
      : nodeEnv.startsWith('st')
        ? 'staging'
        : 'development',
  });
}

// render paths
const renderApplication = () => {
  (async () => {
    const LDProvider = await asyncWithLDProvider({
      clientSideID: launchDarkly,
    });

    const isDevMode = !nodeEnv.startsWith('pr') && !nodeEnv.startsWith('st');

    const appContent = (
      <LDProvider>
        {!nodeEnv.startsWith('pr') && (
          <EnvironmentTab environmentName={nodeEnv.toLocaleLowerCase()}></EnvironmentTab>
        )}
        <Root store={store} />
      </LDProvider>
    );

    root.render(isDevMode ? <StrictMode>{appContent}</StrictMode> : appContent);
  })();
};

const renderLoading = () => {
  root.render(<Loading onRender={renderApplication} />);
};

// boot
if (xsrfTokenCheck()) {
  renderApplication();
} else {
  renderLoading();
}
